/** Enhance native color links with a reversible, synchronized card preview. */
class ModelloCardColors extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('pointerover', (event) => {
      const link = event.target.closest('a[data-preview]');
      if (event.pointerType === 'mouse' && link) this.preview(link);
    }, options);
    this.addEventListener('pointerout', (event) => {
      if (event.pointerType !== 'mouse') return;
      const next = event.relatedTarget?.closest?.('a[data-preview]');
      if (next && this.contains(next)) this.preview(next);
      else {
        const focused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')
          ? document.activeElement.closest('a[data-preview]') : null;
        if (focused) this.preview(focused);
        else this.reset();
      }
    }, options);
    this.addEventListener('focusin', (event) => {
      if (event.target.matches(':focus-visible')) this.preview(event.target.closest('a[data-preview]'));
    }, options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.reset();
    }, options);
    this.addEventListener('click', (event) => {
      // Keep native link/details behavior without triggering card navigation.
      if (event.target.closest('a, summary')) event.stopPropagation();
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const details = this.querySelector('details[open]');
      if (details) {
        details.open = false;
        details.querySelector('summary').focus();
        event.stopPropagation();
      }
      this.reset();
    }, options);
    this.querySelector('details')?.addEventListener('toggle', (event) => {
      if (!event.target.open) this.reset();
    }, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.reset();
    this.overlay?.remove();
    this.overlay = null;
  }

  preview(link) {
    if (!this.hasAttribute('data-preview-enabled') || !link || !this.contains(link) || !link.dataset.title || !link.dataset.price) {
      this.reset();
      return;
    }
    if (this.activeLink === link) return;
    this.reset();
    const generation = this.generation;
    this.activeLink = link;
    const card = this.closest('product-card, [data-modello-color-card]');
    const gallery = card?.querySelector('[ref="cardGallery"]');
    const original = gallery?.querySelector('img:not(.modello-card-color-preview)');
    if (!gallery || !original) return;
    const url = new URL(link.dataset.preview, document.baseURI);
    const width = Math.min(800, Math.ceil(gallery.clientWidth * Math.min(devicePixelRatio || 1, 2) / 100) * 100);
    url.searchParams.set('width', String(Math.max(200, width)));
    const current = new URL(original.currentSrc || original.src, document.baseURI);
    this.timer = setTimeout(() => {
      if (current.pathname === url.pathname) {
        this.applyDetails(card, gallery, link);
        return;
      }
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        if (this.generation !== generation || this.activeLink !== link || !this.isConnected) return;
        if (!this.overlay) {
          this.overlay = document.createElement('img');
          this.overlay.className = 'modello-card-color-preview';
          gallery.append(this.overlay);
        }
        this.overlay.style.objectFit = getComputedStyle(original).objectFit;
        this.overlay.style.objectPosition = getComputedStyle(original).objectPosition;
        this.overlay.src = image.src;
        this.overlay.alt = link.dataset.title;
        this.applyDetails(card, gallery, link);
        const slideshow = gallery.querySelector('slideshow-component');
        if (slideshow) this.setTemporaryAttribute(slideshow, 'aria-hidden', 'true');
        this.overlay.hidden = false;
      };
      image.src = url.href;
    }, 100);
  }

  setTemporaryAttribute(element, name, value) {
    const original = element.getAttribute(name);
    this.restore.push(() => original === null ? element.removeAttribute(name) : element.setAttribute(name, original));
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }

  replaceTemporaryContent(element, ...content) {
    const original = Array.from(element.childNodes);
    this.restore.push(() => element.replaceChildren(...original));
    element.replaceChildren(...content);
  }

  pricePart(value, className, label) {
    const span = document.createElement('span');
    span.className = className;
    if (label) {
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = `${label} `;
      span.append(hidden);
    }
    span.append(document.createTextNode(value));
    return span;
  }

  applyDetails(card, gallery, link) {
    this.restore = [];
    const data = link.dataset;
    this.setTemporaryAttribute(card, 'data-modello-color-preview', '');
    const titleLink = card.querySelector('[ref="productTitleLink"]');
    const title = titleLink?.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"], p') || titleLink;
    if (title) this.replaceTemporaryContent(title, data.title);
    for (const anchor of card.querySelectorAll('[ref="productCardLink"], [ref="productTitleLink"], [ref="cardGalleryLink"]')) {
      this.setTemporaryAttribute(anchor, 'href', link.getAttribute('href'));
      this.setTemporaryAttribute(anchor, 'aria-label', data.title);
    }
    // Native product transitions and quick-add still refer to the original product.
    this.setTemporaryAttribute(card, 'data-product-transition', 'false');
    for (const element of card.querySelectorAll('quick-add-component, .wishlist-button, .product-badges, [ref="volumePricingNote"]')) {
      this.setTemporaryAttribute(element, 'hidden', '');
    }
    for (const container of card.querySelectorAll('[ref="priceContainer"]')) {
      const compareFirst = container.querySelector('.compare-at-price') === container.firstElementChild;
      const price = this.pricePart(data.price, 'price', data.comparePrice ? this.dataset.saleLabel : '');
      const parts = [price];
      if (data.comparePrice) {
        const compare = this.pricePart(data.comparePrice, 'price-item__group price-item--regular compare-at-price', this.dataset.regularLabel);
        if (compareFirst) parts.unshift(compare);
        else parts.push(compare);
      }
      if (data.unitPrice) parts.push(this.pricePart(data.unitPrice, 'unit-price', ''));
      this.replaceTemporaryContent(container, ...parts.flatMap((part, index) => index ? [' ', part] : [part]));
    }
    const soldOut = link.classList.contains('is-sold-out');
    const productBadges = (data.productBadges || '').split(/\s+/);
    const originalBadges = {
      'sold-out': soldOut,
      new: productBadges.includes('new'),
      bestseller: productBadges.includes('bestseller'),
      discount: !!data.comparePrice && Number(data.discountPercent) > 0,
    };
    for (const container of card.querySelectorAll('.modello-original-badges')) {
      this.setTemporaryAttribute(container, 'hidden', Object.values(originalBadges).some(Boolean) ? null : '');
      for (const badge of container.querySelectorAll('[data-original-badge]')) {
        const type = badge.dataset.originalBadge;
        this.setTemporaryAttribute(badge, 'hidden', originalBadges[type] ? null : '');
        if (type === 'discount' && originalBadges.discount) {
          this.replaceTemporaryContent(badge, `−${data.discountPercent}%`);
        }
      }
    }
    if (soldOut || data.comparePrice) {
      const badge = this.querySelector('template[data-preview-badge]').content.firstElementChild.cloneNode(true);
      const text = badge.querySelector('.product-badges__badge');
      text.textContent = soldOut ? this.dataset.soldOutBadge : `−${data.discountPercent}%`;
      text.classList.add(soldOut ? 'color-custom-badge-sold-out' : 'color-custom-badge-sale');
      gallery.append(badge);
      this.restore.push(() => badge.remove());
    }
    const current = Array.from(this.querySelectorAll('.is-current'));
    current.forEach(element => element.classList.remove('is-current'));
    link.classList.add('is-current');
    this.restore.push(() => {
      link.classList.remove('is-current');
      current.forEach(element => element.classList.add('is-current'));
    });
  }

  reset() {
    this.generation = (this.generation || 0) + 1;
    clearTimeout(this.timer);
    this.activeLink = null;
    if (this.overlay) this.overlay.hidden = true;
    this.restore?.reverse().forEach(restore => restore());
    this.restore = [];
  }
}

if (!customElements.get('modello-card-colors')) customElements.define('modello-card-colors', ModelloCardColors);
